import React, { useEffect } from 'react';
import { PayPalScriptProvider, usePayPalScriptReducer } from '@paypal/react-paypal-js';

interface PayPalProviderProps {
  children: React.ReactNode;
}

const PayPalProvider: React.FC<PayPalProviderProps> = ({ children }) => {
  // Get configuration from environment variables
  const clientId = import.meta.env.VITE_PAYPAL_CLIENT_ID;
  const environment = import.meta.env.VITE_PAYPAL_ENVIRONMENT || 'sandbox';
  
  // Validate client ID
  if (!clientId) {
    console.error('PayPal Client ID is missing. Please set VITE_PAYPAL_CLIENT_ID in your environment variables.');
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-red-800">PayPal configuration error. Please contact support.</p>
      </div>
    );
  }
  
  // Check if we should load Fastlane
  const useFastlane = window.location.search.includes('fastlane=true');
  
  // PayPal configuration optimized to prevent window closure issues
  const initialOptions = {
    clientId: clientId,
    currency: 'USD',
    intent: 'capture',
    // Critical for proper popup handling
    commit: true,
    // Enable sandbox mode for testing
    ...(environment === 'sandbox' && {
      'data-client-token': 'sandbox_client_token', // Required for Fastlane in sandbox mode
    }),
    // Load appropriate components based on whether we're using Fastlane
    components: useFastlane ? 'buttons,fastlane' : 'buttons',
    // Enable credit cards and PayPal
    'enable-funding': 'card',
    // Don't disable any funding sources
    'disable-funding': '',
    // Prevent popup issues by setting these options
    'data-namespace': 'paypal_sdk',
    'data-page-type': 'checkout'
  };
  
  return (
    <PayPalScriptProvider options={initialOptions}>
      <PayPalScriptListener>
        {children}
      </PayPalScriptListener>
    </PayPalScriptProvider>
  );
};

// Helper component to listen for PayPal script loading and dispatch events
const PayPalScriptListener: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [{ isResolved }] = usePayPalScriptReducer();
  
  useEffect(() => {
    if (isResolved) {
      // Dispatch a custom event when PayPal SDK is fully loaded
      const event = new Event('paypal-sdk-loaded');
      window.dispatchEvent(event);
      console.log('PayPal SDK loaded successfully');
    }
  }, [isResolved]);
  
  return <>{children}</>;
};

export default PayPalProvider;
